/**
 * 牌桌主场景（§11.2 栅格 / §11.4 排布 / §11.6 动效 / §11.10 无障碍）。
 *
 * 它渲染 `.stage` 与 `.dock` 两个 grid 区（App 的 `table` slot 约定），
 * dock 的内容由外部传入——下注操作区是另一个模块，牌桌不该知道它长什么样。
 *
 * **对 2..8 人全部自适应**：座位坐标一律来自 `resolveTableLayout`，
 * 这里没有任何按人数分支的代码。人数变化只会让 `layoutSeats` 变，几何自己重算。
 *
 * 分成 TableScene / TableStage 两层是因为 `useFx()` 读的是 `FxProvider` 的 context，
 * 同一个组件拿不到自己渲染的 Provider。外层负责几何 + Provider，内层负责内容 + 动效。
 */

import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { CardId, Chips, PublicHandState, SeatIndex, Street } from '@poker/shared'

import { fxKey } from '../fx/FxController.js'
import { FxLayer, FxProvider, useFx, useFxNode, useMotionProfile } from '../fx/FxLayer.js'
import { MOTION } from '../fx/motion.js'
import { useBoardReveal, useTableFx } from '../fx/useTableFx.js'
import {
  selectHeroSeat,
  selectLayoutSeats,
  selectView,
  useSettingsStore,
  useTableStore,
  useUiStore,
  type TableSeatInfo,
} from '../store/index.js'

import { Card } from './Card.js'
import { ChipStack, CommunityBoard, PotDisplay } from './CommunityBoard.js'
import { Seat, type SeatLastAction } from './Seat.js'
import { STREET_TEXT_ZH, cardsLabel, formatChips } from './cardFormat.js'
import {
  bestFiveSet,
  handLabelsBySeat,
  indexBySeat,
  lastActionsBySeat,
  sumStreetCommitted,
  winnerSeats,
} from './tableDerive.js'
import type { SeatAnchor, TableLayout } from './seatGeometry.js'
import { useTableGeometry } from './useTableGeometry.js'
import './table.css'
import { RecursiveRoom } from '../theme/RecursiveRoom.js'

/** 'tap' 模式下看牌 8 秒自动回遮（§11.8 第 7 条） */
const PEEK_TIMEOUT_MS = 8000

export interface TableSceneProps {
  /** dock 区内容（下注操作区）。牌桌只负责给它一块地方。 */
  readonly dock?: ReactNode
  /** 舞台内的额外覆盖层（结算卡、引导层等） */
  readonly stageOverlay?: ReactNode
  /**
   * 当前行动者的截止时刻（服务器时钟）与本次行动的总时长。
   * 牌桌自己拿不到这两个值——它们属于 ActionPrompt，由房间层透传进来；
   * 传 null / 0（本地传手机模式、或房间层还没接上）时倒计时环整体不渲染，
   * 「轮到谁」仍由呼吸辉光表达，不会丢信号。
   */
  readonly turnDeadlineAt?: number | null
  readonly turnMs?: number
  /**
   * 点了别人的座位牌之后做什么（房间层的动作菜单）。
   * 不传就什么都不发生，名牌仍是纯展示的 `<div>` —— 牌桌不知道有哪些动作可做。
   */
  readonly onSeatSelect?: (seat: SeatIndex) => void
  /**
   * 点了自己的座位牌之后做什么（房间层的「我的座位」面板）。
   * 与别人的分开传：什么时候自己那一格可点（有行动权、没轮到自己）是房间层的判断，
   * 不传时自己那一格仍是纯展示。
   */
  readonly onHeroSelect?: (seat: SeatIndex) => void
}

export function TableScene({
  dock,
  stageOverlay,
  turnDeadlineAt = null,
  turnMs = 0,
  onSeatSelect,
  onHeroSelect,
}: TableSceneProps): ReactNode {
  const layoutSeats = useTableStore(selectLayoutSeats)
  const heroSeat = useTableStore(selectHeroSeat)
  const setDensity = useUiStore((s) => s.setDensity)

  const geo = useTableGeometry({ seats: layoutSeats, heroSeat })
  const density = geo.layout?.density

  // 密度由实测舞台高度决定，回写 uiStore 让 .app-shell[data-density] 切 CSS 变量。
  // 只在真的变了才写：zustand 的同值 set 依然会走一遍订阅者。
  useEffect(() => {
    if (density !== undefined) setDensity(density)
  }, [density, setDensity])

  return (
    <FxProvider layoutRef={geo.layoutRef}>
      <TableLayoutContext.Provider value={geo.layout}>
        <div className="stage" ref={geo.stageRef}>
          {/* 毛毡上的花色水印。躺在最底层、对比度极低，只为让桌面不是一块纯色。 */}
          <FeltMark />
          <div className="club-table-caption" aria-hidden="true">
            <span>GÖDEL</span>
            <small>THE PRIVATE CARD ROOM</small>
          </div>
          {geo.layout !== null && (
            <TableStage
              layout={geo.layout}
              turnDeadlineAt={turnDeadlineAt}
              turnMs={turnMs}
              {...(onSeatSelect === undefined ? {} : { onSeatSelect })}
              {...(onHeroSelect === undefined ? {} : { onHeroSelect })}
            />
          )}
          <FxLayer />
          {stageOverlay}
        </div>
        <div className="dock">{dock}</div>
      </TableLayoutContext.Provider>
    </FxProvider>
  )
}

/**
 * 当前牌桌几何（还没量出舞台时为 null）。
 *
 * 给挂在 `stageOverlay` / `dock` 里的房间层组件用 —— 比如聊天气泡要知道每个座位的落点。
 * 几何只算一次、只有一份：外面的组件绝不自己再量一遍舞台。
 */
export const TableLayoutContext = createContext<TableLayout | null>(null)

export function useTableLayout(): TableLayout | null {
  return useContext(TableLayoutContext)
}

// ============================================================================
// 舞台内容
// ============================================================================

interface TableStageProps {
  readonly layout: TableLayout
  readonly turnDeadlineAt: number | null
  readonly turnMs: number
  readonly onSeatSelect?: (seat: SeatIndex) => void
  readonly onHeroSelect?: (seat: SeatIndex) => void
}

function TableStage({ layout, turnDeadlineAt, turnMs, onSeatSelect, onHeroSelect }: TableStageProps): ReactNode {
  const view = useTableStore(selectView)
  const heroSeat = useTableStore(selectHeroSeat)
  const seats = useTableStore((s) => s.seats)
  const veiled = useUiStore((s) => s.veiled)
  const resync = useUiStore((s) => s.resync)
  const soundEnabled = useSettingsStore((s) => s.sound)
  const fx = useFx()
  const profile = useMotionProfile()

  // 遮牌或快照重同步期间不播动画/音效：恢复时不能补一串过期动作。
  const fxEnabled = !veiled && !resync
  useTableFx({ fx, view, heroSeat, enabled: fxEnabled, soundEnabled })
  const { shownCount } = useBoardReveal(view?.board ?? EMPTY_BOARD, fx, fxEnabled)

  const seatInfo = useMemo(() => indexBySeat(seats), [seats])
  const bestFive = useMemo(() => bestFiveSet(view), [view])
  const lastActions = useMemo(() => lastActionsBySeat(view), [view])
  const handLabels = useMemo(() => handLabelsBySeat(view), [view])
  const winners = useMemo(() => winnerSeats(view), [view])

  const streetCommitted = useMemo(() => sumStreetCommitted(view), [view])
  // 推池动画播完前底池数字必须留着：筹码在飞、数字已经归零会让人以为漏了一笔
  const potCleared = useSettlementClear(view, winners.size)
  useAnnouncer(view, heroSeat, shownCount)

  return (
    <>
      {view?.street != null && <div className="table-street">{STREET_TEXT_ZH[view.street]}</div>}

      <CommunityBoard
        layout={layout}
        board={view?.board ?? EMPTY_BOARD}
        shownCount={shownCount}
        bestFive={bestFive}
        reducedMotion={profile.reduced}
      />

      <PotDisplay
        layout={layout}
        potTotal={view?.potTotal ?? 0}
        streetCommitted={potCleared ? (view?.potTotal ?? 0) : streetCommitted}
        pots={view?.pots ?? EMPTY_POTS}
        reducedMotion={profile.reduced}
      />

      {layout.seats.map((anchor) => (
        <SeatAt
          key={anchor.seat}
          anchor={anchor}
          info={seatInfo.get(anchor.seat) ?? null}
          view={view}
          heroSeat={heroSeat}
          lastAction={lastActions.get(anchor.seat) ?? null}
          handLabel={handLabels.get(anchor.seat) ?? null}
          isWinner={winners.has(anchor.seat)}
          bestFive={bestFive}
          turnDeadlineAt={turnDeadlineAt}
          turnMs={turnMs}
          reducedMotion={profile.reduced}
          {...(onSeatSelect === undefined ? {} : { onSeatSelect })}
          {...(onHeroSelect === undefined ? {} : { onHeroSelect })}
        />
      ))}

      {layout.seats.map((anchor) => (
        <BetChip
          key={`bet-${String(anchor.seat)}`}
          anchor={anchor}
          amount={view?.players[anchor.seat]?.committedThisRound ?? 0}
          reducedMotion={profile.reduced}
        />
      ))}

      {view !== null && <DealerButton layout={layout} seat={view.buttonSeat} />}

      {/* 底牌绝不能在遮牌期间留在 DOM 里（红线 #12 第 6 条）—— 条件渲染，不是 opacity */}
      {!veiled && layout.heroHand !== null && heroSeat !== null && (
        <HeroHand
          rectLeft={layout.heroHand.cx}
          rectTop={layout.heroHand.cy}
          holeCards={view?.players[heroSeat]?.holeCards ?? null}
          // 摊牌亮过的牌已经是全场公开信息，再要求「按住才看」就成了对自己人的防偷看
          forceReveal={view?.players[heroSeat]?.hasShownCards ?? false}
          bestFive={bestFive}
        />
      )}
    </>
  )
}

const EMPTY_BOARD: readonly CardId[] = []
const EMPTY_POTS: readonly [] = []

// ============================================================================
// 座位 / 下注 / 庄家钮
// ============================================================================

interface SeatAtProps {
  readonly anchor: SeatAnchor
  readonly info: TableSeatInfo | null
  readonly view: PublicHandState | null
  readonly heroSeat: SeatIndex | null
  readonly lastAction: SeatLastAction | null
  readonly handLabel: string | null
  readonly isWinner: boolean
  readonly bestFive: ReadonlySet<CardId> | null
  /** 见 TableSceneProps：只有正在行动的那个座位会用到 */
  readonly turnDeadlineAt: number | null
  readonly turnMs: number
  readonly reducedMotion: boolean
  readonly onSeatSelect?: (seat: SeatIndex) => void
  readonly onHeroSelect?: (seat: SeatIndex) => void
}

function SeatAt(p: SeatAtProps): ReactNode {
  const player = p.view?.players[p.anchor.seat] ?? null
  const acting = p.view?.actionOn === p.anchor.seat
  const isHero = p.anchor.seat === p.heroSeat
  // 自己那一格与别人的各走各的入口（见 TableSceneProps）
  const onSelect = isHero ? p.onHeroSelect : p.onSeatSelect
  return (
    <Seat
      // 同一座位换人时重建 Seat：新玩家的初始筹码直接落位，不从上一位/空座数字滚过去。
      key={`${String(p.anchor.seat)}:${p.info?.accountId ?? p.info?.name ?? 'empty'}`}
      anchor={p.anchor}
      name={p.info?.name ?? '空位'}
      stack={player?.stack ?? p.info?.stack ?? 0}
      avatarSeed={p.info?.avatarSeed ?? ''}
      accountId={p.info?.accountId ?? ''}
      empty={p.info === null}
      isBot={p.info?.isBot ?? false}
      note={p.info?.note ?? null}
      status={player?.status ?? null}
      hasCards={player?.hasCards ?? false}
      holeCards={player?.holeCards ?? null}
      isHero={isHero}
      isActing={acting}
      isWinner={p.isWinner}
      lastAction={p.lastAction}
      handLabel={p.handLabel}
      bestFive={p.bestFive}
      deadlineAt={acting ? p.turnDeadlineAt : null}
      turnMs={acting ? p.turnMs : 0}
      reducedMotion={p.reducedMotion}
      {...(onSelect === undefined ? {} : { onSelect })}
    />
  )
}

function BetChipImpl({
  anchor,
  amount,
  reducedMotion,
}: {
  anchor: SeatAnchor
  amount: Chips
  reducedMotion: boolean
}): ReactNode {
  const ref = useFxNode(fxKey.bet(anchor.seat))
  void reducedMotion
  if (amount <= 0) return null
  const style: CSSProperties = {
    left: `${anchor.bet.x}px`,
    top: `${anchor.bet.y}px`,
  }
  return (
    // 与 .pot 同一个约定：外层定位、内层才是动效节点
    <div className="stage-anchor bet-anchor" style={style} aria-hidden="true">
      <div className="bet-chip" ref={ref}>
        {/* 真的一小堆筹码（堆高随注码涨），金额永远另有文字，不靠筹码颜色传信息 */}
        <ChipStack amount={amount} />
        <span className="num">{formatChips(amount)}</span>
      </div>
    </div>
  )
}
const BetChip = memo(BetChipImpl)

/**
 * 庄家钮。视觉上是一枚**真的筹码**（象牙盘面 + 边缘刻痕 + 厚度投影，见 table.css），
 * 中间印一个 D —— 而不是直接在毛毡上摆一个字母。
 */
function DealerButton({ layout, seat }: { layout: TableLayout; seat: SeatIndex }): ReactNode {
  const anchor = layout.seatBySeat.get(seat)
  if (anchor === undefined) return null
  const style: CSSProperties = {
    left: `${anchor.dealer.x}px`,
    top: `${anchor.dealer.y}px`,
  }
  return (
    <div className="dealer-btn stage-anchor" style={style} aria-label="庄家位" role="img">
      <span>D</span>
    </div>
  )
}

/**
 * 毛毡正中的花色水印。
 *
 * 对比度压到白 4.5%（table.css 的 .table-mark）：它的职责是让桌面「有东西」，
 * 而不是让人读出来——一旦能读，它就在和公共牌抢视线。
 * 用 SVG 路径而不是 ♠♥♦♣ 文本：这么大一块，万一被系统交给彩色 emoji 字体渲染，
 * 桌子中央会突然出现四个鲜艳的表情符号。
 *
 * 纯装饰，`aria-hidden`，不参与任何动画，也不吃指针事件。
 */
function FeltMark(): ReactNode {
  return (
    <div className="table-mark" aria-hidden="true">
      <RecursiveRoom />
    </div>
  )
}

const SUIT_SPADE = (
  <path d="M12 3C12 3 4.6 9.2 4.6 13.6a3.6 3.6 0 0 0 6.4 2.2c0 2-.9 3.4-2.4 4.8h6.8c-1.5-1.4-2.4-2.8-2.4-4.8a3.6 3.6 0 0 0 6.4-2.2C19.4 9.2 12 3 12 3Z" />
)
const SUIT_HEART = <path d="M12 20.6S3.5 14.6 3.5 9.6a4.3 4.3 0 0 1 8.5-1.3 4.3 4.3 0 0 1 8.5 1.3c0 5-8.5 11-8.5 11Z" />
const SUIT_DIAMOND = <path d="M12 3.4 20 12l-8 8.6L4 12Z" />
const SUIT_CLUB = (
  <g>
    <circle cx="12" cy="8.2" r="3.7" />
    <circle cx="7.6" cy="14" r="3.7" />
    <circle cx="16.4" cy="14" r="3.7" />
    <path d="M12 13.4c0 3.2-.9 5.4-2.6 7.2h5.2c-1.7-1.8-2.6-4-2.6-7.2Z" />
  </g>
)

// ============================================================================
// hero 手牌（按住看牌）
// ============================================================================

interface HeroHandProps {
  readonly rectLeft: number
  readonly rectTop: number
  readonly holeCards: readonly [CardId, CardId] | null
  /** 已在摊牌中亮出：无条件正面朝上 */
  readonly forceReveal: boolean
  readonly bestFive: ReadonlySet<CardId> | null
}

/**
 * 防走光的核心（§11.8 第 4、6 条）：
 * `peekMode === 'hold'` 时必须**持续按住**才翻正面，松手立刻翻回且不做动画；
 * 没在看牌时 `card` 传 null，正面根本不进 DOM。
 */
function HeroHand({ rectLeft, rectTop, holeCards, forceReveal, bestFive }: HeroHandProps): ReactNode {
  const peekMode = useSettingsStore((s) => s.peekMode)
  const veiled = useUiStore((s) => s.veiled)
  const [peeking, setPeeking] = useState(false)
  const timerRef = useRef<number>(0)

  const stop = useCallback(() => {
    setPeeking(false)
    if (timerRef.current !== 0) {
      clearTimeout(timerRef.current)
      timerRef.current = 0
    }
  }, [])

  // 一遮牌就复位：失焦/切后台回来时绝不能还停在「正面」状态
  useEffect(() => {
    if (veiled) stop()
  }, [veiled, stop])
  useEffect(() => () => stop(), [stop])

  const onDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (peekMode === 'hold') {
        setPeeking(true)
        return
      }
      // tap 模式：点一下翻开，8 秒自动回遮
      setPeeking((prev) => {
        if (prev) return false
        if (timerRef.current !== 0) clearTimeout(timerRef.current)
        timerRef.current = globalThis.setTimeout(() => {
          timerRef.current = 0
          setPeeking(false)
        }, PEEK_TIMEOUT_MS)
        return true
      })
    },
    [peekMode],
  )

  const onUp = useCallback(() => {
    if (peekMode === 'hold') stop()
  }, [peekMode, stop])

  if (holeCards === null) return null

  const style: CSSProperties = { left: `${rectLeft}px`, top: `${rectTop}px` }
  const open = peeking || forceReveal
  const shown: readonly (CardId | null)[] = open ? holeCards : [null, null]

  return (
    <div
      className="hero-hand stage-anchor"
      style={style}
      onPointerDown={onDown}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onPointerLeave={onUp}
      role="group"
      aria-label={open ? `你的手牌 ${cardsLabel(holeCards)}` : '你的手牌，按住查看'}
    >
      <div className="hero-hand__slot hero-hand__slot--a">
        <Card
          card={shown[0] ?? null}
          faceUp={open}
          size="hero"
          fxName={fxKey.hero(0)}
          dim={dimmed(bestFive, shown[0])}
          best={highlighted(bestFive, shown[0])}
        />
      </div>
      <div className="hero-hand__slot hero-hand__slot--b">
        <Card
          card={shown[1] ?? null}
          faceUp={open}
          size="hero"
          fxName={fxKey.hero(1)}
          dim={dimmed(bestFive, shown[1])}
          best={highlighted(bestFive, shown[1])}
        />
      </div>
      {!open && <span className="hero-hand__hint">{peekMode === 'hold' ? '按住看牌' : '点击看牌'}</span>}
    </div>
  )
}

function dimmed(best: ReadonlySet<CardId> | null, card: CardId | null | undefined): boolean {
  return best !== null && best.size > 0 && card != null && !best.has(card)
}
function highlighted(best: ReadonlySet<CardId> | null, card: CardId | null | undefined): boolean {
  return best !== null && card != null && best.has(card)
}

/**
 * 结算后把底池清零的时机。等推池动画（420ms + 多赢家 200ms 间隔）走完再清，
 * 一次性 setState，不做逐帧计时。
 */
function useSettlementClear(view: PublicHandState | null, winnerCount: number): boolean {
  const [cleared, setCleared] = useState(false)
  const done = view?.phase === 'complete'
  useEffect(() => {
    if (!done) {
      setCleared(false)
      return undefined
    }
    const wait = MOTION.push.duration + MOTION.push.gap * Math.max(0, winnerCount - 1)
    const id = globalThis.setTimeout(() => setCleared(true), wait)
    return () => clearTimeout(id)
  }, [done, winnerCount])
  return cleared
}

// ============================================================================
// 无障碍播报（§11.10）
// ============================================================================

/**
 * 往 App 提供的两个视觉隐藏 live region 里写文案。
 * 「轮到你」用 assertive（会打断读屏），其余用 polite。
 * 直接写 textContent 而不是渲染 React 节点：这两个 region 是壳层的，牌桌只是往里投稿。
 */
function useAnnouncer(view: PublicHandState | null, heroSeat: SeatIndex | null, shownCount: number): void {
  const prevStreetRef = useRef<Street | null>(null)
  const prevTurnRef = useRef<SeatIndex | null>(null)
  const prevHandRef = useRef<string | null>(null)

  useEffect(() => {
    const polite = document.getElementById('table-announcer')
    const assertive = document.getElementById('turn-announcer')
    if (view === null) return

    if (assertive !== null && view.actionOn !== prevTurnRef.current) {
      prevTurnRef.current = view.actionOn
      assertive.textContent = view.actionOn === heroSeat && heroSeat !== null ? '轮到你行动' : ''
    }

    if (polite === null) return
    if (view.handId !== prevHandRef.current) {
      prevHandRef.current = view.handId
      prevStreetRef.current = null
      polite.textContent = `第 ${String(view.handNo)} 手开始`
      return
    }
    if (view.street !== prevStreetRef.current && view.street !== null && shownCount > 0) {
      prevStreetRef.current = view.street
      polite.textContent = `${STREET_TEXT_ZH[view.street]}，公共牌 ${cardsLabel(view.board.slice(0, shownCount))}`
      return
    }
    if (view.phase === 'complete' && view.result !== null) {
      const winners = view.result.awards.filter((a) => a.amount > 0)
      const first = winners[0]
      if (first !== undefined) {
        polite.textContent = `本手结束，${winners.length > 1 ? '多人平分底池' : `座位 ${String(first.seat)} 赢得 ${formatChips(first.amount)}`}`
      }
    }
  }, [view, heroSeat, shownCount])
}

export default TableScene
